import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const topNavLinks = [
  { label: 'Home', href: '/#home' },
  { label: 'Services', href: '/#services' },
];

const bottomNavLinks = [
  { label: 'Industries', href: '/#industries' },
  { label: 'About', href: '/#about' },
  { label: 'Contact', href: '/#contact' },
];

const productItems = [
  {
    label: 'Omni AI',
    sub: 'AI aggregator platform',
    href: '/products/omni-ai',
    icon: 'ri-sparkling-2-line',
    iconBg: 'bg-accent-100 text-accent-700',
  },
  {
    label: 'RWA Tokenization',
    sub: 'Real estate tokenization',
    href: '/products/rwa-tokenization',
    icon: 'ri-building-4-line',
    iconBg: 'bg-primary-100 text-primary-700',
  },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNavClick = (href: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setMenuOpen(false);

    if (href.startsWith('/#')) {
      const hash = href.replace('/', '');
      if (window.location.pathname === '/') {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/');
        setTimeout(() => {
          const el = document.querySelector(hash);
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      navigate(href);
    }
  };

  const handleProductClick = (href: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setMenuOpen(false);
    setProductsOpen(false);
    navigate(href);
  };

  const linkClass = `px-3 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${
    scrolled
      ? 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
      : 'text-background-100 hover:text-background-50 hover:bg-background-50/10'
  }`;

  const renderProductDropdown = (mobile = false) => (
    <div className={mobile ? 'flex flex-col gap-1 mt-1' : 'absolute top-full left-0 mt-1 w-56 rounded-md bg-background-50 border border-background-200 shadow-lg z-50'}>
      {productItems.map((item, i) => (
        <a
          key={item.href}
          href={item.href}
          onClick={handleProductClick(item.href)}
          className={`flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors ${
            i === 0 ? 'rounded-t-md' : ''
          } ${i === productItems.length - 1 ? 'rounded-b-md' : ''}`}
        >
          <span className={`w-7 h-7 flex items-center justify-center rounded-md ${item.iconBg}`}>
            <i className={`${item.icon} text-sm`}></i>
          </span>
          <div>
            <div className="font-medium">{item.label}</div>
            <div className="text-xs text-foreground-400">{item.sub}</div>
          </div>
        </a>
      ))}
    </div>
  );

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur border-b border-background-200'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <a
          href="/"
          onClick={(e) => { e.preventDefault(); navigate('/'); }}
          className="flex items-center gap-3 cursor-pointer"
        >
          <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-md bg-primary-500 relative overflow-hidden">
            <div className="absolute inset-0 flex">
              <div className="w-1/2 bg-primary-500" />
              <div className="w-1/2 bg-accent-500" />
            </div>
            <span className="relative font-heading font-bold text-background-50 text-lg">S</span>
          </div>
          <div className="flex flex-col leading-none">
            <span className={`font-heading font-bold text-lg md:text-xl transition-colors ${scrolled ? 'text-primary-900' : 'text-background-50'}`}>
              SkyEdge
            </span>
            <span className={`text-[10px] md:text-xs uppercase tracking-widest mt-0.5 transition-colors ${scrolled ? 'text-foreground-500' : 'text-background-200/90'}`}>
              Infotech LLC
            </span>
          </div>
        </a>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {topNavLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={handleNavClick(link.href)}
              className={linkClass}
            >
              {link.label}
            </a>
          ))}

          {/* Products dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setProductsOpen(true)}
            onMouseLeave={() => setProductsOpen(false)}
          >
            <button
              className={`${linkClass} inline-flex items-center gap-1`}
              onClick={() => setProductsOpen(!productsOpen)}
            >
              Products <i className={`ri-arrow-down-s-line text-xs transition-transform ${productsOpen ? 'rotate-180' : ''}`}></i>
            </button>
            <div className={`${productsOpen ? 'opacity-100 visible' : 'opacity-0 invisible'} transition-all duration-200`}>
              {renderProductDropdown()}
            </div>
          </div>

          {bottomNavLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={handleNavClick(link.href)}
              className={linkClass}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="/#contact"
            onClick={(e) => { e.preventDefault(); navigate('/'); setTimeout(() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }), 100); }}
            className="px-5 py-2.5 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer"
          >
            Get in touch
          </a>
        </div>

        <button
          type="button"
          className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md cursor-pointer ${scrolled ? 'text-primary-900' : 'text-background-50'}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`ri-${menuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200 shadow-lg">
          <div className="px-4 py-4 flex flex-col gap-1">
            {topNavLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={handleNavClick(link.href)}
                className="px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-md cursor-pointer"
              >
                {link.label}
              </a>
            ))}

            {/* Mobile products accordion */}
            <div>
              <button
                onClick={() => setProductsOpen(!productsOpen)}
                className="w-full px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-md cursor-pointer flex items-center justify-between"
              >
                Products <i className={`ri-arrow-down-s-line transition-transform ${productsOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {productsOpen && (
                <div className="ml-3">
                  {productItems.map((item) => (
                    <a
                      key={item.href}
                      href={item.href}
                      onClick={handleProductClick(item.href)}
                      className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-md cursor-pointer"
                    >
                      <span className={`w-6 h-6 flex items-center justify-center rounded-md ${item.iconBg}`}>
                        <i className={`${item.icon} text-xs`}></i>
                      </span>
                      <div>
                        <div className="font-medium text-sm">{item.label}</div>
                        <div className="text-xs text-foreground-400">{item.sub}</div>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {bottomNavLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={handleNavClick(link.href)}
                className="px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-md cursor-pointer"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}